import { Loader } from '@progress/kendo-react-indicators'
import classNames from 'classnames'
import { type ExtractAtomValue, useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import * as R from 'remeda'

import { exists, type SurveyGroupFilterGroups } from '~/utils'

import { type onChangeAtom, propAtom, surveyGroupIdAtom } from './atoms'
import {
  SurveyGroupFilterInformationContent,
  type SurveyGroupFilterInformationContentProps,
} from './components/SurveyGroupFilterInformationContent'
import { useMainQuery } from './hooks/useMainQuery'
import { useSurveyGroupFiltersQuery } from './hooks/useSurveyGroupFiltersQuery'
import styles from './SurveyGroupFilterInformation.css'

export type SurveyGroupFilterInformationProps = {
  className?: string
  'data-testid'?: string
  filterGroups: SurveyGroupFilterGroups | undefined
  onChange?: ExtractAtomValue<typeof onChangeAtom>
  onReady?: SurveyGroupFilterInformationContentProps['onReady']
  surveyGroupId: string
}

export const SurveyGroupFilterInformation = ({
  className,
  'data-testid': dataTestId = 'components-features-survey-group-filter-information',
  filterGroups,
  onChange,
  onReady,
  surveyGroupId: surveyGroupIdProp,
}: SurveyGroupFilterInformationProps) => {
  const { isLoading } = useMainQuery()
  useSurveyGroupFiltersQuery()

  const setProp = useSetAtom(propAtom)

  React.useEffect(() => {
    setProp({
      surveyGroupId: surveyGroupIdProp,
      onChange: onChange ?? R.doNothing,
    })

    return () => {
      setProp({
        surveyGroupId: null as unknown as string,
        onChange: R.doNothing,
      })
    }
  }, [onChange, setProp, surveyGroupIdProp])

  const surveyGroupId = useAtomValue(surveyGroupIdAtom)

  if (isLoading || !exists(surveyGroupId)) {
    return (
      <Loader
        className={classNames(styles.loader, className)}
        data-testid={dataTestId}
      />
    )
  }

  return (
    <SurveyGroupFilterInformationContent
      className={className}
      data-testid={dataTestId}
      filterGroups={filterGroups ?? []}
      onReady={onReady}
    />
  )
}
